import React from 'react';
import { Box, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { captionSx, dayLabel, toneOf, seqColor } from './dashboardUtils';

/** Progress ring. `value` is 0–1; the fill colour carries severity, the track is a lighter step of it. */
export const Ring = ({ value, color, size = 148, stroke = 11, track, children }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value || 0));
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track || alpha(color, 0.16)} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${c * v} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </Box>
    </Box>
  );
};

/** Horizontal meter: fill carries severity, track is a lighter step of the same colour. */
export const Meter = ({ value, color, height = 8, track }) => (
  <Box sx={{ height, borderRadius: height / 2, bgcolor: track || alpha(color, 0.16), overflow: 'hidden' }}>
    <Box sx={{ width: `${Math.max(0, Math.min(100, (value || 0) * 100))}%`, height: '100%', borderRadius: height / 2, bgcolor: color, transition: 'width 0.6s ease' }} />
  </Box>
);

const TrendTooltip = ({ active, payload, formatter }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box sx={{ px: 3, py: 2, borderRadius: 2, bgcolor: 'background.paper', color: 'text.primary', border: (t) => `1px solid ${t.palette.divider}`, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
      <Typography sx={{ ...captionSx, fontSize: '0.75rem' }}>{p.label}</Typography>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{formatter(p)}</Typography>
    </Box>
  );
};

/** 2px line + area wash, hairline horizontal grid, end-dot with a surface ring. One series, no legend. */
export const TrendArea = ({ data, dataKey, color, formatter, domain, height = 130, onBand = false }) => {
  const theme = useTheme();
  const tone = toneOf(theme, onBand);
  const stroke = color || tone.accent;
  const last = data[data.length - 1];
  const gradId = `trend-${dataKey}-${onBand ? 'band' : 'card'}`;
  return (
    <Box sx={{ width: '100%', height, position: 'relative' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 14, left: 14, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={onBand ? 0.45 : 0.22} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={tone.line} strokeDasharray="" />
          <XAxis dataKey="label" tickLine={false} axisLine={{ stroke: tone.line }} tick={{ fontSize: 11, fill: tone.muted }} interval="preserveStartEnd" />
          <YAxis hide domain={domain || [0, 'auto']} />
          <RTooltip content={<TrendTooltip formatter={formatter} />} cursor={{ stroke: tone.line }} />
          <Area
            type="monotone" dataKey={dataKey} stroke={stroke} strokeWidth={2.5} fill={`url(#${gradId})`} isAnimationActive={false}
            dot={(p) => (p.index === data.length - 1
              ? <circle key={p.key} cx={p.cx} cy={p.cy} r={5.5} fill={stroke} stroke={tone.surface} strokeWidth={2.5} />
              : <g key={p.key} />)}
            activeDot={{ r: 5.5, fill: stroke, stroke: tone.surface, strokeWidth: 2.5 }}
          />
        </AreaChart>
      </ResponsiveContainer>
      {last && (
        <Typography sx={{ position: 'absolute', top: 0, right: 14, fontSize: '0.75rem', fontWeight: 600, color: tone.text }}>
          {formatter(last)}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Horizontal bars: label on the left, thin bar, value at the tip. One hue,
 * unless `colorOf` says otherwise (e.g. non-billable in a lighter step).
 */
export const HBars = ({ rows, labelOf, valueOf, valueLabel, colorOf, subOf, onClick, activeKey, keyOf = (r) => r.id, thickness = 10, labelWidth = '38%' }) => {
  const theme = useTheme();
  const max = Math.max(...rows.map(valueOf), 0) || 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {rows.map((r) => {
        const v = valueOf(r);
        const key = keyOf(r);
        const active = activeKey != null && activeKey === key;
        const clickable = typeof onClick === 'function';
        return (
          <Box
            key={key}
            onClick={clickable ? () => onClick(r) : undefined}
            sx={{
              display: 'grid', gridTemplateColumns: `${labelWidth} 1fr auto`, alignItems: 'center', gap: 3,
              px: 2, py: 1, mx: -2, borderRadius: 2, cursor: clickable ? 'pointer' : 'default',
              bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              '&:hover': clickable ? { bgcolor: alpha(theme.palette.text.primary, 0.04) } : {},
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 500 }} title={labelOf(r)}>{labelOf(r)}</Typography>
              {subOf && <Typography noWrap sx={{ ...captionSx, fontSize: '0.7rem' }}>{subOf(r)}</Typography>}
            </Box>
            <Box sx={{ height: thickness, borderRadius: thickness / 2, bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
              <Box sx={{ width: `${Math.max(1.5, (v / max) * 100)}%`, height: '100%', borderRadius: `0 ${thickness / 2}px ${thickness / 2}px 0`, bgcolor: colorOf ? colorOf(r) : theme.palette.primary.main, transition: 'width 0.4s ease' }} />
            </Box>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'right' }}>
              {valueLabel ? valueLabel(r) : v}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Donut for 3–4 states. `slices` = [{ key, label, value, color }] in fixed
 * order. Segments get a 2px surface gap; the centre carries the total.
 */
export const Donut = ({ slices, size = 150, thickness = 18, centerLabel, children }) => {
  const theme = useTheme();
  const total = slices.reduce((t, s) => t + (s.value || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={alpha(theme.palette.text.primary, 0.06)} strokeWidth={thickness} />
        {total > 0 && slices.filter((s) => s.value > 0).map((s) => {
          const len = (s.value / total) * c;
          const el = (
            <circle
              key={s.key} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={s.color} strokeWidth={thickness}
              strokeDasharray={`${Math.max(0, len - 2)} ${c - Math.max(0, len - 2)}`} strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += len;
          return el;
        })}
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {children || (
          <>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>{total.toLocaleString()}</Typography>
            {centerLabel && <Typography sx={{ ...captionSx, fontSize: '0.7rem', mt: 0.5 }}>{centerLabel}</Typography>}
          </>
        )}
      </Box>
    </Box>
  );
};

/** Inline row of small day cells for a service: share of people who logged that day. */
export const MiniHeat = ({ cells, today }) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  return (
    <Box sx={{ display: 'inline-flex', gap: '3px' }}>
      {cells.map((c) => {
        const open = c.due === 0 || c.date >= today;
        const share = open ? 0 : c.logged / c.due;
        const title = open
          ? `${dayLabel(c.date, { weekday: 'short', day: 'numeric' })} · ${c.date === today ? 'today' : 'upcoming'}`
          : `${dayLabel(c.date, { weekday: 'short', day: 'numeric' })} · ${c.logged} of ${c.due} logged (${Math.round(share * 100)}%)`;
        return (
          <Tooltip key={c.date} title={title} arrow>
            <Box sx={{
              width: 18, height: 18, borderRadius: '4px',
              bgcolor: open ? 'transparent' : seqColor(share, dark),
              border: open ? `1.5px ${c.date === today ? 'solid' : 'dashed'} ${alpha(theme.palette.text.primary, 0.18)}` : 'none',
            }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};
